import React from 'react'
import { ArrowBack, ArrowForward, AccountBalanceWallet, EventRepeat, Inventory2, Tune } from '@mui/icons-material'
import s from './AdminActionsV2.module.css'

/*
 * Admin actions tab of subscriber_v2 — what admins changed on this account,
 * as a timeline: newest first, grouped by day, each entry with its kind
 * (package, balance, renewal date), the message and who did it. Chips filter
 * by kind. Same data as the classic tab (component/admin_logs/AdminsActions):
 * `subscriber.history`, from /admin-dashboard/admin_story/:id.
 *
 * Sorted by `createdTime` — the classic table sorted by `paidOn`, a field
 * these entries do not have, so it kept the server's order.
 */

type Story = { id: number | string; adminLogin?: string; message?: string; action?: string; createdTime?: string }

const PER_PAGE = 8

const KINDS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  package_change: { label: 'Package', color: 'var(--ok)', icon: <Inventory2 sx={{ fontSize: 16 }} /> },
  balance_change: { label: 'Balance', color: 'var(--primary)', icon: <AccountBalanceWallet sx={{ fontSize: 16 }} /> },
  renewal_date_change: { label: 'Renewal date', color: 'var(--warn)', icon: <EventRepeat sx={{ fontSize: 16 }} /> },
}
const OTHER = { label: 'Other', color: 'var(--text3)', icon: <Tune sx={{ fontSize: 16 }} /> }
const kindOf = (action?: string) => KINDS[String(action || '')] || OTHER
const actionName = (action?: string) => {
  const known = KINDS[String(action || '')]
  if (known) return known.label
  const t = String(action || '').replace(/_/g, ' ').trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : 'Other'
}

const pad = (n: number) => String(n).padStart(2, '0')
const timeOf = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`
const dayOf = (d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })
const initials = (v?: string) => String(v || '?').replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?'

export function AdminActionsV2({ subscriber }: { subscriber: any }) {
  const [filter, setFilter] = React.useState('all')
  const [page, setPage] = React.useState(0)

  const all: Story[] = React.useMemo(() => {
    const list: Story[] = Array.isArray(subscriber?.history) ? subscriber.history : []
    return [...list].sort((a, b) => new Date(b.createdTime || 0).getTime() - new Date(a.createdTime || 0).getTime())
  }, [subscriber?.history])

  // one chip per kind that actually occurs, most frequent first
  const chips = React.useMemo(() => {
    const counts = new Map<string, number>()
    all.forEach((st) => {
      const k = KINDS[String(st.action || '')] ? String(st.action) : 'other'
      counts.set(k, (counts.get(k) || 0) + 1)
    })
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [all])

  const shown = filter === 'all' ? all : all.filter((st) => (KINDS[String(st.action || '')] ? String(st.action) : 'other') === filter)
  const pages = Math.max(1, Math.ceil(shown.length / PER_PAGE))
  const rows = shown.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)
  const admins = new Set(all.map((st) => st.adminLogin).filter(Boolean)).size

  let lastDay = ''

  return (
    <div className={s.panel}>
      <div className={s.head}>
        <h3>Admin actions</h3>
        <span className={s.sub}>{all.length} change{all.length === 1 ? '' : 's'}{admins ? ` · by ${admins} admin${admins === 1 ? '' : 's'}` : ''}</span>
      </div>

      {chips.length > 1 && (
        <div className={s.chips}>
          {[['all', all.length] as [string, number], ...chips].map(([k, n]) => {
            const meta = k === 'all' ? { label: 'All', color: 'var(--primary)' } : (KINDS[k] || OTHER)
            const on = filter === k
            return (
              <button
                key={k}
                type="button"
                className={`${s.chip} ${on ? s.chipOn : ''}`}
                style={on ? { background: meta.color, borderColor: meta.color } : undefined}
                onClick={() => { setFilter(k); setPage(0) }}
              >
                {meta.label} <span>{n}</span>
              </button>
            )
          })}
        </div>
      )}

      {!shown.length ? (
        <div className={s.empty}>No admin actions on this account</div>
      ) : (
        <div className={s.timeline}>
          {rows.map((st, i) => {
            const d = st.createdTime ? new Date(st.createdTime) : null
            const valid = d && !Number.isNaN(d.getTime())
            const day = valid ? dayOf(d as Date) : ''
            const showDay = !!day && day !== lastDay
            if (day) lastDay = day
            const kind = kindOf(st.action)
            return (
              <React.Fragment key={st.id ?? i}>
                {showDay && <div className={s.day}>{day}</div>}
                <div className={s.item}>
                  <span className={s.icon} style={{ color: kind.color, background: `color-mix(in srgb, ${kind.color} 13%, transparent)` }}>
                    {kind.icon}
                  </span>
                  <div className={s.body}>
                    <div className={s.msg}>{st.message || '—'}</div>
                    <div className={s.meta}>
                      <span className={s.tag} style={{ color: kind.color, borderColor: `color-mix(in srgb, ${kind.color} 30%, transparent)` }}>
                        {actionName(st.action)}
                      </span>
                      {st.adminLogin && (
                        <span className={s.by}>
                          <span className={s.avatar}>{initials(st.adminLogin)}</span>
                          {st.adminLogin}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={s.time}>{valid ? timeOf(d as Date) : '—'}</span>
                </div>
              </React.Fragment>
            )
          })}
        </div>
      )}

      {pages > 1 && (
        <div className={s.pager}>
          <span>{page * PER_PAGE + 1}–{Math.min(shown.length, (page + 1) * PER_PAGE)} of {shown.length}</span>
          <div className={s.pagerBtns}>
            <button type="button" className={s.iconBtn} disabled={page === 0} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
              <ArrowBack sx={{ fontSize: 16 }} />
            </button>
            <button type="button" className={s.iconBtn} disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
              <ArrowForward sx={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
